// What this deal's implementation ("buy-in") fee should be, and what it is.
//
// The number comes from `/api/reports/implementation-fee?deal_id=`, not from
// arithmetic here: resolving the end customer's account count against the
// governing agreement term's banded fee schedule is a four-step walk the server
// already does for every deal at once, and a second implementation on this
// screen is how the card and the reconciliation table start disagreeing about
// the same deal.
//
// WHAT THIS CARD IS FOR is the gap. A suggestion nobody acted on is just a
// number, so the card's job is to say which of three things is true — the
// agreement prices this deal and nothing bills it, the agreement prices it and
// the line matches, or the agreement CANNOT price it and here is the reason —
// and to make the first of those one click to fix.
//
// It never writes. "Add as term line" opens the ordinary term-line form
// with the fields filled in; the fee is not booked until somebody submits it.

import type { ReactNode } from "react";
import { isResolvedImplementationFeeRow } from "@crm/shared";
import type {
  Currency,
  ImplFeeUnresolvedReason,
  ImplementationFeeRow,
  ResolvedImplementationFeeRow,
} from "@crm/shared";
import styles from "./ImplementationFeeCard.module.css";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { formatCalendarDate } from "../../format/calendarDate.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";
import { useDealImplementationFee } from "../../api/queries.js";
import type { TermLinePrefill } from "./TermLineFormDialog.js";

/**
 * The label the prefilled term line gets. Fixed rather than derived from the
 * band, because the line is a charge and the band is how its size was decided —
 * putting "Up to 75,000 accounts" on an invoice line would explain our pricing
 * model to the customer's accounts-payable department.
 */
const IMPL_FEE_LABEL = "Implementation fee";

/**
 * What a reader can do about each unresolved reason, and whose job it is. The
 * distinction is the point of the enum: `no_account_count` is a question for the
 * account team, `no_tier_match` is a gap in the agreement's schedule, and
 * `no_agreement` is a partner-configuration problem — three different screens.
 */
function unresolvedMessage(reason: ImplFeeUnresolvedReason | null, partnerName: string): string {
  // One line each, saying what is missing and who fixes it.
  //
  // These were paragraphs — thirty to sixty words apiece explaining the state
  // of the world. On a deal where several panels are empty at once that is
  // most of the page, and `no_fee_schedule` (by far the commonest, and not a
  // problem at all) was the longest of them. The reasoning behind each lives
  // in docs/crm-term-lines.md, which is where somebody who wants it will look.
  if (reason === null) {
    return "No fee priced, and no reason given — worth reporting.";
  }
  switch (reason) {
    case "no_account_count":
      return "No account count on this deal, and that is what selects a band. Edit the deal to add it.";
    case "no_agreement":
      return `No agreement pinned, and ${partnerName} has no default — no commercial term resolves until that is fixed.`;
    case "no_term":
      return "No agreement term in force on this deal's start date.";
    case "no_fee_schedule":
      return "This agreement prices no implementation fee.";
    case "no_tier_match":
      return "No band covers an account count this large. Add one on the agreement term.";
    case "currency_mismatch":
      return "The covering band is priced in another currency, and nothing here converts.";
    default:
      return assertNever(reason, "implementation fee unresolved reason");
  }
}

/** How the recorded lines compare with the suggestion. Drives the badge. */
type FeeStanding = "matches" | "not_billed" | "differs";

function standingOf(row: ResolvedImplementationFeeRow): FeeStanding {
  if (row.recorded_line_count === 0) return "not_billed";
  return row.variance_minor === 0 ? "matches" : "differs";
}

function standingBadge(standing: FeeStanding): { tone: Tone; label: string } {
  switch (standing) {
    case "matches":
      return { tone: "good", label: "Billed as agreed" };
    case "not_billed":
      return { tone: "warn", label: "Not billed yet" };
    case "differs":
      return { tone: "info", label: "Differs from the agreement" };
    default:
      return assertNever(standing, "implementation fee standing");
  }
}

/**
 * Whether this deal has an implementation fee to talk about at all.
 *
 * THE PANEL USED TO STAND ON EVERY DEAL, and on a deal whose agreement bands no
 * fee it said "No fee resolves" — an answer to a question nobody asked about
 * that engagement. Kingsport carries no fee schedule and bills no milestone;
 * the honest rendering there is no panel.
 *
 * A REASON IS NOT THE SAME AS AN ABSENCE. `no_account_count`, `no_tier_match`
 * and `currency_mismatch` are all raised by a schedule that EXISTS and cannot
 * reach this deal — that is a gap somebody has to close, so it stays on screen.
 * `no_agreement`, `no_term` and `no_fee_schedule` say there is no schedule to
 * price against, which is the ordinary state of an engagement with no buy-in.
 *
 * Two things override that. A deal that already bills a fee has a figure
 * nothing can check, which is worth saying out loud. And a deal that bills a
 * milestone clause carries a fee whatever the agreement bands.
 *
 * A row with no figures and NO reason is a server-side inconsistency. It stays
 * visible: an inconsistency nobody sees is one nobody reports.
 */
export function implementationFeeApplies(
  row: ImplementationFeeRow,
  hasMilestoneClause: boolean,
): boolean {
  if (isResolvedImplementationFeeRow(row)) return true;
  if (row.recorded_line_count > 0 || hasMilestoneClause) return true;
  switch (row.unresolved_reason) {
    case "no_agreement":
    case "no_term":
    case "no_fee_schedule":
      return false;
    case "no_account_count":
    case "no_tier_match":
    case "currency_mismatch":
    case null:
      return true;
    default:
      return assertNever(row.unresolved_reason, "implementation fee unresolved reason");
  }
}

export interface ImplementationFeeCardProps {
  dealId: string;
  /** D8: the card reports in the deal's currency, since that is what a line would be in. */
  currency: Currency;
  partnerName: string;
  /** A clause billed as its milestones land — the shape a buy-in fee takes. */
  hasMilestoneClause: boolean;
  /**
   * Null for a reader without `crm_editor`. The whole card still renders — the
   * number is worth knowing whether or not you can act on it — only the button
   * goes away, because offering an action whose save would be refused is worse
   * than not offering it.
   */
  onAddLine: ((prefill: TermLinePrefill) => void) | null;
}

export function ImplementationFeeCard({
  dealId,
  currency,
  partnerName,
  hasMilestoneClause,
  onAddLine,
}: ImplementationFeeCardProps): ReactNode {
  const report = useDealImplementationFee(dealId, currency);

  return (
    <QueryBoundary query={report} label="this deal's implementation fee">
      {(data) => {
        // `?.` on a field the schema declares as required is not paranoia
        // here: `lenientParse` in the API client deliberately hands back an
        // unvalidated body rather than blanking a screen when the response
        // and the contract disagree, so a client that indexes straight into
        // a nested array is a client that throws on exactly the drift that
        // policy exists to survive.
        const row = data.rows?.[0];
        if (row === undefined) {
          return (
            <FeeCard>
              <p className={styles.reason}>
                This deal is not in the implementation-fee report. That should not happen — every
                deal gets a row, priced or not.
              </p>
            </FeeCard>
          );
        }
        if (!implementationFeeApplies(row, hasMilestoneClause)) return null;
        return (
          <FeeCard>
            <FeeDetail
              row={row}
              currency={currency}
              partnerName={partnerName}
              onAddLine={onAddLine}
            />
          </FeeCard>
        );
      }}
    </QueryBoundary>
  );
}

/** The panel around whatever the report had to say. */
function FeeCard({ children }: { children: ReactNode }): ReactNode {
  return (
    <Card>
      <CardHeader title="Implementation fee" />
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function FeeDetail({
  row,
  currency,
  partnerName,
  onAddLine,
}: {
  row: ImplementationFeeRow;
  currency: Currency;
  partnerName: string;
  onAddLine: ((prefill: TermLinePrefill) => void) | null;
}): ReactNode {
  // The five resolved figures are null together, which is a cross-field
  // invariant the document cannot declare — so the contract carries the
  // narrowing as a predicate instead. Past it the branch below reads five
  // numbers with no `?? 0` standing in for a figure the report computed.
  if (!isResolvedImplementationFeeRow(row)) {
    return (
      <div className={styles.unresolved}>
        <Badge shape="chip" tone="neutral">
          No fee resolves
        </Badge>
        <p className={styles.reason}>{unresolvedMessage(row.unresolved_reason, partnerName)}</p>
        {/* Recorded lines are shown even when nothing prices them: "this deal
            bills $25,000 for implementation and we cannot tell you whether that
            is right" is a more useful thing to say than silence. */}
        {row.recorded_line_count > 0 && (
          <p className={styles.reason}>
            {`It already bills ${formatMoney(row.recorded_gross_minor, currency)} across ${
              row.recorded_line_count === 1 ? "one line" : `${row.recorded_line_count} lines`
            }, which nothing here can check against the agreement.`}
          </p>
        )}
      </div>
    );
  }

  const standing = standingBadge(standingOf(row));

  return (
    <div className={styles.detail}>
      <div className={styles.headline}>
        <span className={`${styles.amount} mono`}>
          {formatMoney(row.suggested_gross_minor, currency)}
        </span>
        <Badge shape="chip" tone={standing.tone}>
          {standing.label}
        </Badge>
      </div>

      <dl className={styles.derivation}>
        <Derivation label="Customer size">
          {`${formatCount(row.customer_account_count ?? 0)} accounts — ${bandDescription(row)}`}
        </Derivation>

        <Derivation label="Priced by">
          {row.agreement_name === null || row.term_effective_date === null
            ? "—"
            : `${row.agreement_name}, term effective ${formatCalendarDate(row.term_effective_date)}`}
        </Derivation>

        {/* The band's own figure, in the SCHEDULE's currency, whenever that is
            not the deal's. The converted number above is what a term line would
            carry; this is the number written in the agreement, and a reader who
            checks one against the other needs both. */}
        {row.tier_currency !== null && row.tier_currency !== currency && (
          <Derivation label="As written in the agreement">
            {`${formatMoney(row.tier_fee_minor ?? 0, row.tier_currency)} — converted at the rate in force on ${formatCalendarDate(row.as_of)}`}
          </Derivation>
        )}

        <Derivation label="Our share">
          {`${formatPercent(row.impl_split_pct)} · ${formatMoney(row.suggested_our_share_minor, currency)}`}
        </Derivation>

        <Derivation label="Currently billed">
          {row.recorded_line_count === 0
            ? "Nothing — no implementation-fee line on this deal"
            : `${formatMoney(row.recorded_gross_minor, currency)} across ${
                row.recorded_line_count === 1 ? "one line" : `${row.recorded_line_count} lines`
              }${
                row.variance_minor === 0
                  ? ""
                  : ` · ${formatMoney(row.variance_minor, currency, { signDisplay: "exceptZero" })} against the agreement`
              }`}
        </Derivation>
      </dl>

      {/* Offered only when nothing bills it yet. Once a line exists, "add
          another" is almost never what anyone means — a wrong amount is edited
          on the line itself, and a second implementation line would double-bill
          a fee the agreement charges once. */}
      {onAddLine !== null && row.recorded_line_count === 0 && (
        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={() =>
              onAddLine({ label: IMPL_FEE_LABEL, amountMinor: row.suggested_gross_minor })
            }
          >
            Add as term line
          </Button>
          <span className={styles.actionHint}>
            Opens the term-line form filled in. Nothing is billed until you save it.
          </span>
        </div>
      )}
    </div>
  );
}

/** "the up-to-75,000 band" / "the open-ended top band", in words a reader can check. */
function bandDescription(row: ResolvedImplementationFeeRow): string {
  return row.tier_max_accounts === null
    ? "the open-ended top band"
    : `the up-to-${formatCount(row.tier_max_accounts)} band`;
}

function Derivation({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className={styles.derivationRow}>
      <dt className={styles.derivationLabel}>{label}</dt>
      <dd className={styles.derivationValue}>{children}</dd>
    </div>
  );
}
